import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSocket } from "../realtime";

type SocketStatus = "connecting" | "connected" | "reconnecting" | "disconnected";

export function useSocketConnection() {
  const { socket } = useSocket();

  const [status, setStatus] = useState<SocketStatus>("disconnected");
  const [attempts, setAttempts] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);
  const [autoRetry, setAutoRetry] = useState(true);

  // internal debounce so we don't spam manual reconnects
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRetryTimer = () => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
  };

  const connect = useCallback(() => {
    if (!socket) return;
    // Ensure auto reconnection is enabled
    (socket.io.opts as any).reconnection = true;
    setLastError(null);
    if (!socket.connected) {
      setStatus("connecting");
      socket.connect();
    }
  }, [socket]);

  const disconnect = useCallback(() => {
    if (!socket) return;
    clearRetryTimer();
    // disable auto reconnection for a manual disconnect
    (socket.io.opts as any).reconnection = false;
    socket.disconnect();
    setStatus("disconnected");
  }, [socket]);

  const retry = useCallback(() => {
    if (!socket) return;
    clearRetryTimer();
    setLastError(null);
    setAttempts((n) => n + 1);
    setStatus("reconnecting");
    (socket.io.opts as any).reconnection = true;
    socket.connect();
  }, [socket]);

  const enableAutoRetry = useCallback((on: boolean) => {
    setAutoRetry(on);
    if (socket) (socket.io.opts as any).reconnection = on;
    // if we just turned it on and we're disconnected, kick a retry
    if (on && socket && !socket.connected) {
      clearRetryTimer();
      retryTimer.current = setTimeout(() => retry(), 500);
    }
  }, [socket, retry]);

  // Wire socket events
  useEffect(() => {
    if (!socket) return;

    // The socket may have already connected before this effect ran (context timing).
    // Sync immediately so the badge doesn't stay red when the socket is live.
    if (socket.connected) {
      clearRetryTimer();
      setStatus("connected");
      setAttempts(0);
      setLastError(null);
    }

    const onConnect = () => {
      clearRetryTimer();
      setStatus("connected");
      setAttempts(0);
      setLastError(null);
    };

    const onDisconnect = (reason: any) => {
      setStatus("disconnected");
      setLastError(typeof reason === "string" ? reason : String(reason));
      // nudge a retry if enabled (socket.io will also retry, this just ensures we kick quickly)
      if (autoRetry) {
        clearRetryTimer();
        retryTimer.current = setTimeout(() => retry(), 1000);
      }
    };

    const onReconnectAttempt = (n: number) => {
      setAttempts(n);
      setStatus("reconnecting");
    };

    const onReconnect = () => {
      setStatus("connected");
      setLastError(null);
    };

    const onConnectError = (err: any) => {
      setLastError(err?.message ?? String(err));
      // we let socket.io continue its own backoff; optional extra nudge:
      if (autoRetry && !socket.connected) {
        clearRetryTimer();
        retryTimer.current = setTimeout(() => retry(), 1500);
      }
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.io.on("reconnect_attempt", onReconnectAttempt);
    socket.io.on("reconnect", onReconnect);
    socket.on("connect_error", onConnectError);

    return () => {
      clearRetryTimer();
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.io.off("reconnect_attempt", onReconnectAttempt);
      socket.io.off("reconnect", onReconnect);
      socket.off("connect_error", onConnectError);
    };
  }, [socket, autoRetry, retry]);

  const connected = status === "connected";

  return useMemo(
    () => ({
      socket,
      status,
      connected,
      attempts,
      lastError,
      autoRetry,
      connect,
      retry,
      disconnect,
      enableAutoRetry,
    }),
    [socket, status, connected, attempts, lastError, autoRetry, connect, retry, disconnect, enableAutoRetry]
  );
}
